import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { bool, func, object, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../../util/reactIntl';

import css from './VoucherInput.module.css';

const VoucherInput = props => {
  const {
    intl,
    onApplyVoucher,
    voucherValidation: voucherValidationFromAPI,
    fetchLineItemsInProgress,
    currency,
    storedDiscountCode,
  } = props;

  const { currentUser } = useSelector(state => state.user);
  const isLoggedIn = !!currentUser;

  const [showVoucherInput, setShowVoucherInput] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [validationDisplay, setValidationDisplay] = useState({ status: null, message: null });
  const [lastValidatedCode, setLastValidatedCode] = useState(null); // Track which code was validated
  const [isAutoApplied, setIsAutoApplied] = useState(false);
  const autoAppliedRef = useRef(false);

  // Auto-fill stored percentage discount from partner QR
  useEffect(() => {
    if (storedDiscountCode && !autoAppliedRef.current && !voucherCode) {
      autoAppliedRef.current = true;
      setVoucherCode(storedDiscountCode);
      setShowVoucherInput(true);
      setIsAutoApplied(true);
      setLastValidatedCode(storedDiscountCode);
      setValidationDisplay({
        status: 'validating',
        message: intl.formatMessage({ id: 'BookingTimeForm.validating' }),
      });
      onApplyVoucher(storedDiscountCode);
    }
  }, [storedDiscountCode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Get wallet balance from Redux (calculated by server)
  const aggregatedWalletBalance = useSelector(state => state.ListingPage?.aggregatedWalletBalance);
  const remainingWalletBalance = useSelector(state => state.ListingPage?.remainingWalletBalance);
  
  // Fallback: check if user has wallet in same currency for immediate display
  const userPrivateData = currentUser?.attributes?.profile?.privateData || {};
  const walletBalances = userPrivateData.walletBalances || {};
  const walletInSameCurrency = walletBalances[currency] || 0;
  
  // Display logic:
  // 1. If remainingWalletBalance exists (number, could be 0) → show remaining (after discount)
  // 2. Else if aggregatedWalletBalance exists → show full balance (before discount)
  // 3. Else fallback to same-currency wallet
  const walletBalance = 
    typeof remainingWalletBalance === 'number' ? remainingWalletBalance : // After discount applied
    (aggregatedWalletBalance || walletInSameCurrency); // Before discount or fallback

  // Effect to process validation from API
  // Only updates message when user EXPLICITLY validates by clicking "Apply"
  // DO NOT show validation messages while user is just typing
  useEffect(() => {
    // CRITICAL: Only process validation if user clicked "Apply" button
    // lastValidatedCode is ONLY set when user clicks Apply (see handleApplyVoucher)
    // This prevents showing wallet validation messages while user is typing
    if (!lastValidatedCode) {
      return;
    }
    
    // Only process if not fetching
    if (fetchLineItemsInProgress) {
      return;
    }

    // If no validation result, clear display
    if (!voucherValidationFromAPI) {
      setValidationDisplay({ status: null, message: null });
      return;
    }

    // Skip if we've already processed this validation (prevents duplicate processing)
    if (lastValidatedCode !== voucherCode?.toUpperCase()) {
      return;
    }

    // Process validation from API (user clicked Apply and we have response)
    
    // CRITICAL: If validation failed, clear the voucher code to trigger wallet fallback
    if (!voucherValidationFromAPI.valid) {
      // Clear the code after a brief delay to allow user to see the error message
      setTimeout(() => {
        setVoucherCode('');
        setLastValidatedCode(null); // Reset so user can try again
        onApplyVoucher(''); // This triggers re-fetch with empty voucher (wallet fallback)
      }, 2000); // 2 seconds to read error message
    }
    
    if (voucherValidationFromAPI.valid) {
      // Success case
      const { discountType, percentage, originalAmount, originalCurrency } = voucherValidationFromAPI;
      let successMessage;
      
      if (discountType === 'PERCENTAGE' && percentage) {
        successMessage = intl.formatMessage(
          { id: 'BookingTimeForm.voucherSuccessPercentage' },
          { percentage }
        );
      } else if (discountType === 'FIXED' && originalAmount) {
        // FIXED code: Show amount, will be converted to wallet at checkout
        const voucherAmount = Math.abs(originalAmount / 100);
        const voucherCurrency = originalCurrency || currency;
        const roundedVoucherAmount = Math.round(voucherAmount);
        const localizedAmount = intl.formatNumber(roundedVoucherAmount, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        successMessage = intl.formatMessage(
          { id: 'BookingTimeForm.codeValidCredit' },
          { amount: `${voucherCurrency} ${localizedAmount}` }
        );
      }

      setValidationDisplay({
        status: 'success',
        message: successMessage,
      });
    } else {
      // Error case - map error codes to messages
      const errorCode = voucherValidationFromAPI.error;
      let errorId = 'BookingTimeForm.voucherInvalid';

      if (errorCode === 'NOT_FOUND') {
        errorId = 'BookingTimeForm.voucherNotFound';
      } else if (errorCode === 'EXPIRED') {
        errorId = 'BookingTimeForm.voucherExpired';
      } else if (errorCode === 'ALREADY_USED') {
        errorId = 'BookingTimeForm.voucherAlreadyUsed';
      } else if (errorCode === 'USAGE_LIMIT_REACHED' || errorCode === 'LIMIT_REACHED') {
        errorId = 'BookingTimeForm.voucherLimitReached';
      } else if (errorCode === 'INVALID' || errorCode === 'INACTIVE' || errorCode === 'DISABLED') {
        errorId = 'BookingTimeForm.voucherInvalid';
      } else if (errorCode === 'NOT_ELIGIBLE' || errorCode === 'NOT_INVITED') {
        errorId = 'BookingTimeForm.voucherNotEligible';
      } else if (errorCode === 'DOMAIN_MISMATCH') {
        errorId = 'BookingTimeForm.voucherDomainMismatch';
      } else if (errorCode === 'WALLET_PRIORITY') {
        errorId = 'BookingTimeForm.voucherBlockedByWallet';
      } else if (errorCode === 'VALIDATION_FAILED') {
        errorId = 'BookingTimeForm.voucherValidationFailed';
      }

      setValidationDisplay({
        status: 'error',
        message: intl.formatMessage({ id: errorId }),
      });
    }
  }, [voucherValidationFromAPI, fetchLineItemsInProgress, intl, currency, voucherCode, lastValidatedCode, onApplyVoucher]);

  const handleApplyVoucher = () => {
    if (!voucherCode || voucherCode.trim() === '') {
      setValidationDisplay({ status: null, message: null });
      setLastValidatedCode(null);
      return;
    }

    // CRITICAL: Mark that user clicked Apply - this triggers validation message display
    // Set lastValidatedCode BEFORE calling API so useEffect knows to process the response
    setLastValidatedCode(voucherCode.toUpperCase());
    
    // Set validating state
    setValidationDisplay({ 
      status: 'validating', 
      message: intl.formatMessage({ id: 'BookingTimeForm.validating' })
    });

    // Call parent's validation handler (will trigger FormSpy → handleOnChange → API call)
    onApplyVoucher(voucherCode);
  };

  const handleToggleChange = checked => {
    setShowVoucherInput(checked);
    if (!checked) {
      setVoucherCode('');
      setValidationDisplay({ status: null, message: null });
      setLastValidatedCode(null);
      setIsAutoApplied(false);
      onApplyVoucher(''); // Clear voucher in parent
    }
  };

  // Calculate display credit
  // aggregatedWalletBalance already includes all wallet credit (existing + new codes) in listing currency
  // remainingWalletBalance is what remains AFTER this booking discount is applied
  // Don't add them - they're not meant to be combined!
  // Show wallet credit EXCEPT when:
  // - Input is open AND no aggregatedWalletBalance (no FIXED code added yet)
  // - This allows showing existing wallet + FIXED code aggregated value when FIXED code is applied
  const hasAggregatedBalance = aggregatedWalletBalance && aggregatedWalletBalance > 0;
  const displayCredit = walletBalance > 0 && (!showVoucherInput || hasAggregatedBalance);
  
  return (
    <div className={css.root}>
      {/* Discount Code Toggle */}
      <div className={css.voucherToggleContainer}>
        <label className={css.voucherToggleLabel}>
          <input
            type="checkbox"
            checked={showVoucherInput}
            onChange={e => handleToggleChange(e.target.checked)}
            className={css.voucherToggleCheckbox}
          />
          <FormattedMessage id="BookingTimeForm.haveDiscountCode" />
        </label>
      </div>

      {/* Voucher Code Input */}
      {showVoucherInput && (
        <div className={css.voucherInputContainer}>
          {/* Voucher code input */}
          <div className={css.voucherInputWrapper}>
            <input
              type="text"
              value={voucherCode}
              onChange={e => {
                setVoucherCode(e.target.value.toUpperCase());
                if (isAutoApplied) setIsAutoApplied(false);
              }}
              placeholder={intl.formatMessage({ id: 'BookingTimeForm.voucherPlaceholder' })}
              className={`${css.voucherInput} ${
                validationDisplay.status === 'success' ? css.voucherInputSuccess :
                validationDisplay.status === 'error' ? css.voucherInputError :
                validationDisplay.status === 'validating' ? css.voucherInputValidating :
                ''
              }`}
            />
            <button
              type="button"
              onClick={handleApplyVoucher}
              disabled={fetchLineItemsInProgress || !voucherCode.trim()}
              className={css.voucherSubmitButton}
            >
              {fetchLineItemsInProgress ? (
                <FormattedMessage id="BookingTimeForm.validating" />
              ) : (
                <FormattedMessage id="BookingTimeForm.applyCode" />
              )}
            </button>
          </div>

          {/* Validation Messages */}
          {validationDisplay.status === 'success' && validationDisplay.message && (
            <div className={css.voucherSuccess}>
              {isAutoApplied && (
                <span className={css.autoAppliedLabel}>
                  <FormattedMessage id="BookingTimeForm.partnerDiscountLabel" />
                </span>
              )}
              {validationDisplay.message}
            </div>
          )}
          {validationDisplay.status === 'error' && validationDisplay.message && (
            <div className={css.voucherError}>{validationDisplay.message}</div>
          )}
          {validationDisplay.status === 'validating' && validationDisplay.message && (
            <div className={css.voucherValidating}>{validationDisplay.message}</div>
          )}
          
          {/* Login Required Info */}
          {!isLoggedIn && validationDisplay.status === 'success' && (
            <div className={css.voucherInfo}>
              <FormattedMessage id="BookingTimeForm.loginRequiredForVoucher" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

VoucherInput.defaultProps = {
  fetchLineItemsError: null,
  voucherValidation: null,
  storedDiscountCode: null,
};

VoucherInput.propTypes = {
  intl: intlShape.isRequired,
  onApplyVoucher: func.isRequired,
  fetchLineItemsError: object,
  fetchLineItemsInProgress: bool.isRequired,
  currency: string.isRequired,
  voucherValidation: object,
  storedDiscountCode: string,
};

export default VoucherInput;
